import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { PADDING, project } from '../utils/constants'

const ChartPath = ({ dataPoints, bounds, lineColor, animated, width, height }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (animated) {
      setShow(false)
      const timer = setTimeout(() => setShow(true), 100)
      return () => clearTimeout(timer)
    } else {
      setShow(true)
    }
  }, [animated, dataPoints])

  const pathData = useMemo(() => {
    return dataPoints.map((d, i) => {
      const { x, y } = project(width, height, i, d.y, bounds, dataPoints.length)
      return `${i === 0 ? 'M' : 'L'}${x},${y}`
    }).join(' ')
  }, [dataPoints, bounds, width, height])

  const areaPathData = useMemo(() => {
    if (!pathData) return ''
    const { x: xStart } = project(width, height, 0, bounds.minY, bounds, dataPoints.length)
    const { x: xEnd } = project(width, height, dataPoints.length - 1, bounds.minY, bounds, dataPoints.length)
    return `${pathData} L${xEnd},${height - PADDING.b} L${xStart},${height - PADDING.b} Z`
  }, [pathData, bounds, dataPoints.length, width, height])

  if (dataPoints.length === 0) return null

  return (
    <>
      <defs>
        <linearGradient id='areaGradient' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={lineColor} stopOpacity={0.4} />
          <stop offset='100%' stopColor={lineColor} stopOpacity={0.05} />
        </linearGradient>
        <filter id='shadow' x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='0' dy='2' stdDeviation='3' floodColor={lineColor} floodOpacity='0.5' />
        </filter>
      </defs>

      {/* Area Path */}
      <path
        d={areaPathData}
        fill='url(#areaGradient)'
        style={{
          opacity: show ? 1 : 0,
          transition: 'opacity 1s ease-in-out'
        }}
      />

      {/* Line Path */}
      <path
        d={pathData}
        fill='none'
        stroke={lineColor}
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
        filter='url(#shadow)'
        style={{
          transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
          strokeDasharray: 500000,
          strokeDashoffset: show ? 0 : 500000
        }}
      />
    </>
  )
}

ChartPath.propTypes = {
  dataPoints: PropTypes.array.isRequired,
  bounds: PropTypes.object.isRequired,
  lineColor: PropTypes.string.isRequired,
  animated: PropTypes.bool,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired
}

export default ChartPath
